import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { Clock3, LogOut, Sparkles, BadgeIndianRupee, ShieldCheck } from "lucide-react";
import { openRazorpayCheckout, fmtMoney } from "@/lib/razorpay";
import CouponField from "@/components/CouponField";

export default function PendingApproval() {
  const { user, logout } = useAuth();
  const [paying, setPaying] = useState(false);
  const [price, setPrice] = useState({ amount: 200000, currency: "INR" });
  const [coupon, setCoupon] = useState(null);
  const payable = coupon ? { amount: coupon.amount, currency: coupon.currency } : price;
  const needsPayment = user?.subscription_status === "pending_payment";
  const trialEnded = needsPayment && user?.trial_ends_at && !user?.trial_active;

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => {
      const p = (data.pricing && data.pricing[user?.country || "IN"]) || data.default_pricing || { amount: data.amount, currency: data.currency };
      if (p) setPrice(p);
    }).catch(() => {});
  }, [user?.country]);

  const payNow = async () => {
    setPaying(true);
    try {
      const { data: order } = await api.post("/payments/create-order", { coupon_code: coupon?.code });
      openRazorpayCheckout({
        order: { ...order, user_id: user.id },
        prefill: { name: user?.name, email: user?.email },
        onVerified: () => {
          toast.success("Payment successful! Welcome aboard.");
          window.location.href = "/student";
        },
        onDismiss: () => setPaying(false),
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <header className="px-6 py-5 flex items-center justify-between">
        <Logo size="sm" />
        <button
          data-testid="pending-logout"
          onClick={logout}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-cloud text-ink text-sm font-bold hover:bg-cloud"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-lg w-full text-center">
          <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${needsPayment ? "bg-watermelon-light text-watermelon" : "bg-lemon/40 text-ink"}`}>
            {needsPayment ? <BadgeIndianRupee className="w-10 h-10" /> : <Clock3 className="w-10 h-10" />}
          </div>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-bold text-sm mb-4 ${needsPayment ? "bg-watermelon text-white" : "bg-lemon text-ink"}`}>
            <Sparkles className="w-4 h-4" /> {needsPayment ? "One step left!" : "Almost there!"}
          </div>

          {needsPayment ? (
            <>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink mb-4">
                Complete your payment
              </h1>
              <p data-testid="pending-message" className="text-lg text-stone mb-8">
                Hi <strong className="text-ink">{user?.name}</strong>! {user?.trial_active
                  ? "You're on your 1-day free trial. Pay your membership fee now so learning never stops."
                  : trialEnded
                    ? "Your 1-day free trial has ended — pay your membership fee to continue learning."
                    : "Your account is ready — pay your first month's membership fee to activate it instantly."}
              </p>
              {user?.trial_active && (
                <Link to="/student" data-testid="back-to-trial-dashboard" className="inline-block mb-6 text-sm font-bold text-sky-hover hover:underline">
                  ← Continue my free trial
                </Link>
              )}
              <div className="p-6 bg-white rounded-3xl card-tactile text-left mb-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-bold text-ink">Monthly membership fee</span>
                  <span data-testid="pending-fee-amount" className="font-heading text-3xl font-bold text-watermelon">{fmtMoney(payable.amount, payable.currency)}</span>
                </div>
                <div className="mb-5">
                  <CouponField country={user?.country || "IN"} applied={coupon} onChange={setCoupon} />
                </div>
                <ul className="text-sm text-stone space-y-2 mb-5">
                  <li>· Pay securely with UPI, card, or netbanking (Razorpay)</li>
                  <li>· Account activates the moment payment succeeds</li>
                  <li className="flex items-center gap-1.5 font-semibold text-ink"><ShieldCheck className="w-4 h-4 text-leaf" /> Fully refundable within 3 days</li>
                </ul>
                <button
                  data-testid="pay-now-button"
                  onClick={payNow}
                  disabled={paying}
                  className="w-full h-14 rounded-full bg-watermelon text-white font-heading font-bold text-lg btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
                >
                  {paying ? "Opening checkout…" : `Pay ${fmtMoney(payable.amount, payable.currency)} & Activate`}
                </button>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink mb-4">
                Waiting for teacher approval
              </h1>
              <p data-testid="pending-message" className="text-lg text-stone mb-8">
                Hi <strong className="text-ink">{user?.name}</strong>! Your account is created and a teacher will review it soon. Once approved, you'll be able to sign in and start practicing.
              </p>
              <div className="p-5 bg-white rounded-3xl card-tactile text-left">
                <div className="text-sm font-bold text-ink mb-2">What happens next?</div>
                <ol className="text-sm text-stone space-y-2 list-decimal list-inside">
                  <li>A teacher reviews your signup and picks your starting level.</li>
                  <li>You'll be able to sign in and see your first exercise.</li>
                  <li>Practice daily and climb the leaderboard!</li>
                </ol>
              </div>
              <p className="mt-6 text-sm text-stone">
                Come back and sign in later — this usually takes less than a day.
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
